import {Injectable} from '@angular/core';

/**
 * Interface that settings components must implement to support unsaved changes detection
 */
export interface IHasUnsavedChanges {
  /**
   * Returns true if the component has unsaved changes
   */
  haveAnyEditsBeenMade: boolean;

  /**
   * Shows a modal asking the user if they want to discard changes
   * @returns Promise that resolves to true if user wants to proceed (discard changes), false if user cancels
   */
  onShowModalUnsavedChanges(): Promise<boolean>;
}

/**
 * Service to manage unsaved changes detection across settings components.
 *
 * Settings components should register themselves with this service when they become active,
 * and the sidebar/navigation can check for unsaved changes before allowing navigation.
 */
@Injectable({
  providedIn: 'root',
})
export class UnsavedChangesService {

  #currentComponent: IHasUnsavedChanges | null = null;

  /**
   * Registers the currently active settings component
   * @param component The component that implements IHasUnsavedChanges
   */
  registerComponent(component: IHasUnsavedChanges | null): void {
    this.#currentComponent = component;
  }

  /**
   * Checks if the current component has unsaved changes
   * @returns true if there are unsaved changes, false otherwise
   */
  hasUnsavedChanges(): boolean {
    return this.#currentComponent?.haveAnyEditsBeenMade ?? false;
  }

  /**
   * Shows the unsaved changes modal if there are unsaved changes
   * @returns Promise that resolves to true if navigation should proceed (no changes or user discarded),
   *          false if navigation should be cancelled (user cancelled modal)
   */
  async checkUnsavedChanges(): Promise<boolean> {
    if (!this.#currentComponent) {
      return true; // No component registered, allow navigation
    }

    if (!this.#currentComponent.haveAnyEditsBeenMade) {
      return true; // No unsaved changes, allow navigation
    }

    // There are unsaved changes, show modal and return user's choice
    return await this.#currentComponent.onShowModalUnsavedChanges();
  }

  /**
   * Clears the currently registered component
   * Should be called when a component is destroyed
   */
  unregisterComponent(): void {
    this.#currentComponent = null;
  }
}
